import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useWatch } from 'react-hook-form';

import { FormSection } from 'common/components/FormSection';
import { ColorPickerField, FileField } from 'common/forms/Field';
import { TControllerProps } from 'common/types';
import {
    CERTIFICATE_TEMPLATE_FILE_TYPES,
    CERTIFICATE_TEMPLATE_MAX_SIZE,
} from 'common/constants/files';
import { IFileFragment, ITemplate } from 'graphql/types';
import { CertificateTemplateSelectField } from 'certificate/forms/Field';
import { TCertificateFormValues } from 'certificate/types';

interface IProps extends TControllerProps<TCertificateFormValues> {
    customTemplateFile?: IFileFragment | null;
    onCustomTemplateFileUpload?(file: IFileFragment | null): void;
}

export const TemplateSection = ({
    control,
    customTemplateFile,
    onCustomTemplateFileUpload,
}: IProps) => {
    const [translate] = useTranslation();

    const template = useWatch<TCertificateFormValues, 'template'>({
        control,
        name: 'template',
    });

    const isCustomTemplate = template === ITemplate.Custom;

    return (
        <FormSection
            renderExpanded
            description={
                <Box>{translate('certificateForm.templateDescription')}</Box>
            }
            title={translate('certificateForm.templateTitle')}
        >
            <Grid container spacing={2}>
                <Grid size={6}>
                    <CertificateTemplateSelectField
                        control={control}
                        name="template"
                    />
                </Grid>

                <Grid size={3}>
                    <ColorPickerField
                        fullWidth
                        control={control}
                        label={translate('colors.primary')}
                        name="primaryColor"
                    />
                </Grid>

                <Grid size={3}>
                    <ColorPickerField
                        fullWidth
                        control={control}
                        label={translate('colors.secondary')}
                        name="secondaryColor"
                    />
                </Grid>
            </Grid>

            {isCustomTemplate && (
                <Box sx={{ mt: 3 }}>
                    <FileField
                        acceptedFileTypes={CERTIFICATE_TEMPLATE_FILE_TYPES}
                        control={control}
                        displayFile={customTemplateFile}
                        helperText={translate('fileHelperText', {
                            count: CERTIFICATE_TEMPLATE_MAX_SIZE,
                        })}
                        id="customTemplateFile"
                        label={translate('file')}
                        maxUploadSize={CERTIFICATE_TEMPLATE_MAX_SIZE}
                        name="customTemplateId"
                        onFileUpload={onCustomTemplateFileUpload}
                    />
                </Box>
            )}
        </FormSection>
    );
};
